// The words on a bundle (docs/design/bundles.md): how many members have a draft ("3 of 7
// drafted", never said at zero and never as a shortfall), the progress of "Draft all"
// ("Writing 3 of 7…"), and each member's draft state in one word. Pure, so it is tested
// without a DOM.
import type { CardView, DraftRequestStatus } from "@shared/api";
import type { WorkStatus } from "@shared/core";
import { draftCoverage } from "@shared/drafts";

/** "3 of 7 drafted"; null when none is drafted yet (nothing to say, no zero shown). */
export function draftedWords(drafted: number, total: number): string | null {
  if (drafted <= 0 || total <= 0) return null;
  return drafted >= total ? `All ${total} drafted` : `${drafted} of ${total} drafted`;
}

/** "Writing 3 of 7…" while Draft all asks for one draft after another. */
export function writingWords(at: number, of: number): string {
  return `Writing ${at} of ${of}…`;
}

/** "Draft all 7"; "Draft the last one" when one is left ("Draft all 1" reads oddly). */
export function draftAllWords(count: number): string {
  return count === 1 ? "Draft the last one" : `Draft all ${count}`;
}

/**
 * One member's draft as the bundle sees it: `ready` (a draft is written), `question` (Claude
 * asked a question instead, which the card answers), `writing`, `none`, or `unknown` while the
 * card itself has not loaded and nothing was asked yet.
 */
export type MemberDraftState = "ready" | "question" | "writing" | "none" | "unknown";

const FINISHED = new Set<WorkStatus>(["completed", "failed", "cancelled", "stale"]);

/**
 * From the member's full card when it is loaded (its blocks and newest draft work), else from the
 * last answer the server gave to a draft request for it this session.
 */
export function memberDraftState(
  card: Pick<CardView, "kind" | "ask" | "blocks" | "drafting"> | null | undefined,
  requested: DraftRequestStatus | null,
): MemberDraftState {
  const asked = requested === "queued" || requested === "working";
  if (!card) {
    if (requested === "ready") return "ready";
    return asked ? "writing" : "unknown";
  }
  const coverage = draftCoverage(card);
  if (coverage === "draft") return "ready";
  if (coverage === "question") return "question";
  const work = card.drafting;
  if (work?.status === "queued" || work?.status === "working") return "writing";
  // An ask's "queued" outlives the work it started: once that work ended, the card speaks.
  if (asked && !(work && FINISHED.has(work.status))) return "writing";
  return "none";
}

const STATE_WORD: Record<MemberDraftState, string | null> = {
  ready: "draft ready",
  question: "a question for you",
  writing: "writing",
  none: "no draft yet",
  unknown: null,
};

/** The member row's draft word; null while it is not known yet. */
export function draftStateWord(state: MemberDraftState): string | null {
  return STATE_WORD[state];
}

/** Draft all asks for these: members with no draft, no question and nothing being written. */
export function wantsDraft(state: MemberDraftState): boolean {
  return state === "none" || state === "unknown";
}
